import React, { useCallback, useEffect, useState, useMemo } from "react";
import { User } from "auth/AuthProvider";
import { Role } from "types";
import { User as UserIcon } from "lucide-react";
import { Dialog } from "components/dialog";
import api, { fetchAllUsersAPI, fetchUsersCurrentWorkspaceAPI } from "api/axios";
import toast from "react-hot-toast";
import { handleAxiosAndCaughtError } from "utils";
import { Image } from "components/Image";
import UsersAddingFormTable from "./UsersAddingFormTable";
import { useTranslation } from "react-i18next";

// Simplified interface - only what we need for the API
interface UserToAdd {
  email: string;
  roleId?: string;
}

interface AddUsersFormProps {
  isOpen: boolean;
  onClose: () => void;
  onUsersAdded: () => void;
}

const AddUsersForm: React.FC<AddUsersFormProps> = ({ isOpen, onClose, onUsersAdded }) => {
  const { t } = useTranslation();
  const [selectedUsers, setSelectedUsers] = useState<UserToAdd[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // All users fetched from API
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersSkip, setUsersSkip] = useState(0);
  const USERS_TAKE = 20;

  // Current workspace users to filter out
  const [workspaceUsers, setWorkspaceUsers] = useState<User[]>([]);

  // Filtered users (all users excluding workspace users)
  const users = useMemo(() => {
    const workspaceUserIds = workspaceUsers.map((user: User) => user.id);
    return allUsers.filter((user: User) => !workspaceUserIds.includes(user.id));
  }, [allUsers, workspaceUsers]);

  // Should show load more button
  const shouldShowLoadMore = useMemo(() => {
    return !usersLoading && !usersError && allUsers.length < usersTotal;
  }, [usersLoading, usersError, allUsers.length, usersTotal]);

  // Fetch current workspace users to filter them out
  const fetchWorkspaceUsers = useCallback(async () => {
    try {
      const response = await fetchUsersCurrentWorkspaceAPI({
        take: 999,
        skip: 0,
        handler: {},
      });
      setWorkspaceUsers(response.items);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  }, []);

  // Fetch users for load more (pagination)
  const fetchMoreUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await fetchAllUsersAPI({
        take: USERS_TAKE,
        skip: usersSkip,
        handler: {
          query: userSearchTerm,
        },
      });
      
      setAllUsers(prev => [...prev, ...response.items]);
      setUsersSkip(prev => prev + USERS_TAKE);
      setUsersTotal(response.total);
    } catch (err) {
      setUsersError("Failed to load more users");
      handleAxiosAndCaughtError(err);
    } finally {
      setUsersLoading(false);
    }
  }, [userSearchTerm, usersSkip]);

  // Fetch initial users (reset pagination)
  const fetchInitialUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await fetchAllUsersAPI({
        take: USERS_TAKE,
        skip: 0,
        handler: {
          query: userSearchTerm,
        },
      });
      
      setAllUsers(response.items);
      setUsersSkip(USERS_TAKE);
      setUsersTotal(response.total);
    } catch (err) {
      setUsersError("Failed to load users");
      handleAxiosAndCaughtError(err);
    } finally {
      setUsersLoading(false);
    }
  }, [userSearchTerm]);

  // Fetch available roles
  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const { data } = await api.get<{ total: number; items: Role[] }>("/groups", {
        params: { take: 999, skip: 0 },
      });
      setAvailableRoles(data.items);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
      fetchRoles();
      fetchWorkspaceUsers();
  }, [ fetchRoles, fetchWorkspaceUsers]);

  useEffect(() => {
    if (isOpen) {
      const handler = setTimeout(() => {
        // Reset pagination when search term changes
        setUsersSkip(0);
        setAllUsers([]);
        fetchInitialUsers();
      }, 500); // Debounce search
      return () => clearTimeout(handler);
    }
  }, [isOpen, userSearchTerm, fetchInitialUsers]);

  const handleAddUsers = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUsers.length === 0) return;
    
    // Validate that all users have roles selected
    const usersWithoutRoles = selectedUsers.filter(user => !user.roleId);
    if (usersWithoutRoles.length > 0) {
      toast.error(t("workspaces.usersDialog.mustSelectRolesForAll"));
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Prepare users array with email and group_id
      const usersToAdd = selectedUsers.map(user => {
        if (!user.roleId) {
          throw new Error("No role selected for user");
        }
        return {
          email: user.email,
          group_id: user.roleId
        };
      });

      // Add users to workspace
      await api.post(`/workspaces/users`, {
        users: usersToAdd
      });
      
      onUsersAdded();
      setIsSubmitting(false);
      onClose();
      setSelectedUsers([]);
      setIsUserDropdownOpen(false);
      setUserSearchTerm("");
      toast.success(t("workspaces.usersDialog.addedSuccess"));
    } catch (err) {
      toast.error(t("workspaces.usersDialog.addedFailed"));
      setIsSubmitting(false);
      handleAxiosAndCaughtError(err);
    }
  };

  // Multi-select logic
  const handleUserToggle = (user: User) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.email === user.email);
      if (isSelected) {
        return prev.filter((u) => u.email !== user.email);
      } else {
        // Add user with default role (first available role)
        const defaultRole = availableRoles[0]?.id;
        return [...prev, {
          email: user.email,
          roleId: defaultRole
        }];
      }
    });
  };

  const addEmailToTable = (email: string) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || selectedUsers.some(u => u.email === trimmedEmail)) {
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error(t("validation.invalidEmail"));
      return;
    }

    const defaultRole = availableRoles[0]?.id;
    const newUser: UserToAdd = {
      email: trimmedEmail,
      roleId: defaultRole
    };

    setSelectedUsers(prev => [...prev, newUser]);
    setUserSearchTerm("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmailToTable(userSearchTerm);
    }
  };

  const removeUser = (email: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.email !== email));
  };

  const updateUserRole = (email: string, roleId: string) => {
    setSelectedUsers((prev) => 
      prev.map(user => 
        user.email === email 
          ? { ...user, roleId: roleId }
          : user
      )
    );
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(userSearchTerm);
  const isEmailInFiltered = filteredUsers.some(
    (u) => u.email === userSearchTerm
  );
  const isEmailInSelected = selectedUsers.some(
    (u) => u.email === userSearchTerm
  );

  // Check if all users have roles selected
  const isFormValid = useMemo(() => {
    return selectedUsers.length > 0 && selectedUsers.every(user => user.roleId);
  }, [selectedUsers]);

  const handleClose = () => {
    setSelectedUsers([]);
    setIsUserDropdownOpen(false);
    setUserSearchTerm("");
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} setIsOpen={handleClose}>
      <form
        onSubmit={handleAddUsers}
        className="flex w-full min-w-[280px] max-w-[500px] flex-col gap-4 sm:min-w-[400px]"
      >
        <div className="border-b border-gray-200 pb-3">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {t("workspaces.usersDialog.title")}
          </h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t("workspaces.usersDialog.subtitle")}
          </p>
        </div>

        {/* User Search and Selection */}
        <div className="relative">
          <div className="relative">
            <UserIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              className="block w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-4 text-sm placeholder-gray-500 shadow-sm transition-all duration-200 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 dark:focus:border-brand-400"
              placeholder={t("workspaces.usersDialog.searchPlaceholder")}
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => setIsUserDropdownOpen(true)}
            />
          </div>
          {/* User Dropdown */}
          {isUserDropdownOpen && (
            <div className="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-2 shadow-xl dark:border-gray-600 dark:bg-gray-800">
              {usersLoading && users.length === 0 && (
                <div className="flex items-center justify-center p-3 text-gray-500 sm:p-4">
                  {t("workspaces.usersDialog.loadingUsers")}
                </div>
              )}
              {usersError && users.length === 0 && (
                <div className="flex flex-col items-center p-3 sm:p-4">
                  <span className="text-sm text-red-500">{usersError}</span>
                  <button
                    type="button"
                    className="mt-1.5 text-sm text-blue-500 underline sm:mt-2"
                    onClick={fetchInitialUsers}
                  >
                    {t("common.retry")}
                  </button>
                </div>
              )}
              {filteredUsers.length > 0
                ? filteredUsers.map((user) => {
                    const isSelected = selectedUsers.some(
                      (u) => u.email === user.email
                    );
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleUserToggle(user)}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          isSelected ? "bg-brand-50 dark:bg-brand-900/20" : ""
                        }`}
                      >
                        <Image
                          className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-200 text-xs font-bold text-blue-700 sm:h-8 sm:w-8 sm:text-sm"
                          alt={user.name || user.email}
                        />

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium text-gray-900 dark:text-white sm:text-sm">
                            {user.name}
                          </p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                            {user.email}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400 sm:h-4 sm:w-4">
                            ✓
                          </div>
                        )}
                      </button>
                    );
                  })
                : !isValidEmail && (
                    <div className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 sm:px-4 sm:py-3 sm:text-sm">
                      {t("workspaces.usersDialog.noUsersFound")}
                    </div>
                  )}
              {isValidEmail && !isEmailInFiltered && !isEmailInSelected && (
                <button
                  type="button"
                  onClick={() => addEmailToTable(userSearchTerm)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-brand-50 dark:hover:bg-brand-900/20"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
                    <UserIcon className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                      {t("workspaces.usersDialog.addUserByEmail")}
                    </p>
                    <p className="truncate text-xs text-brand-600 dark:text-brand-400">
                      {userSearchTerm}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center rounded-full bg-brand-100 px-2 py-1 text-xs font-medium text-brand-800 dark:bg-brand-900/30 dark:text-brand-200">
                      {t("workspaces.usersDialog.add")}
                    </span>
                  </div>
                </button>
              )}
              {shouldShowLoadMore && (
                <button
                  type="button"
                  className="w-full p-1.5 text-center text-xs text-blue-500 hover:underline sm:p-2 sm:text-sm"
                  onClick={fetchMoreUsers}
                >
                  {t("users.loadMore")}
                </button>
              )}
              {usersLoading && users.length > 0 && (
                <div className="flex items-center justify-center p-1.5 text-xs text-gray-400 sm:p-2">
                  {t("users.loadingMore")}
                </div>
              )}
            </div>
          )}
          {/* Click outside to close dropdown */}
          {isUserDropdownOpen && (
            <div
              className="fixed inset-0 z-0"
              onClick={() => setIsUserDropdownOpen(false)}
            />
          )}
        </div>

        {/* Selected Users Table */}
        <UsersAddingFormTable
          selectedUsers={selectedUsers}
          availableRoles={availableRoles}
          rolesLoading={rolesLoading}
          onRemoveUser={removeUser}
          onUpdateUserRole={updateUserRole}
        />

        <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:gap-4">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 rounded-xl border border-gray-300 px-4 py-3 font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500/20 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:border-gray-500"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="flex-1 rounded-xl bg-brand-500 px-4 py-3 font-semibold text-white shadow-sm transition-all duration-200 hover:bg-brand-600 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-brand-500"
            disabled={isSubmitting || !isFormValid}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                {t("workspaces.usersDialog.adding")}
              </div>
            ) : (
              t("workspaces.usersDialog.addUsers")
            )}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default AddUsersForm;
